import type { ApiError } from "@portfolio/types";

const baseUrl =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  "";

export interface ApiOptions extends RequestInit {
  /** Optional JSON body (auto-serialised). Wins over `body` if both supplied. */
  json?: unknown;
  /** Multipart FormData body — wins over `json` and `body` if supplied. */
  formData?: FormData;
}

export class ApiErrorResponse extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem("portfolio.auth.token", token);
  } else {
    localStorage.removeItem("portfolio.auth.token");
  }
}

export function loadStoredAuthToken(): string | null {
  if (authToken) return authToken;
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem("portfolio.auth.token");
  if (stored) authToken = stored;
  return authToken;
}

async function request<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = `${baseUrl}${path}`;
  const headers = new Headers(opts.headers as HeadersInit | undefined);
  headers.set("Accept", "application/json");

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.json !== undefined) {
    body = JSON.stringify(opts.json);
    headers.set("Content-Type", "application/json");
  } else if (opts.body !== undefined) {
    body = opts.body ?? undefined;
  }

  const token = loadStoredAuthToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, { ...opts, headers, body });
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const err = payload as ApiError & { details?: unknown };
    throw new ApiErrorResponse(
      response.status,
      err?.error || response.statusText,
      err?.details
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: ApiOptions) => request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, opts?: ApiOptions) =>
    request<T>(path, { ...opts, method: "POST" }),
  put: <T>(path: string, opts?: ApiOptions) =>
    request<T>(path, { ...opts, method: "PUT" }),
  delete: <T>(path: string, opts?: ApiOptions) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};

// -----------------------------------------------------------------------------
// Convenience helpers for analytics events
// -----------------------------------------------------------------------------

export type AnalyticsPayload = {
  eventType: "page_view" | "project_click" | "cv_download";
  entityId?: number | null;
  path?: string | null;
  referrer?: string | null;
  userAgent?: string | null;
};

export function trackEvent(payload: AnalyticsPayload) {
  try {
    void api.post("/api/analytics", { json: payload });
  } catch {
    // Fire-and-forget — analytics should never break the UI.
  }
}
